/**
 * start.ts — the start dialogue.
 *
 * A centered overlay card shown over a dimmed lake before the episode begins.
 * The viewer sets the hyperparameters and presses Play. Defaults match the
 * values that produced reliable convergence in testing:
 *   - 100 rounds, 0.5 s/round → a ~50-second episode
 *   - sliding window N = 20   → fast adaptation to drift
 *   - exploration β = 0.1     → low exploration once learning kicks in
 *   - drift δ = 0.01          → visible wandering, slow enough to track
 *
 * The card sits inside `.lake-wrap` (passed in by main) so it overlays the
 * lake, not the whole window. `onPlay(config)` fires when the viewer presses
 * Play; `onRemove` lets main detach the card.
 */

/** Hyperparameters chosen at the start of an episode. */
export interface EpisodeConfig {
  /** Total number of bandit rounds. */
  rounds: number;
  /** Per-round wall-clock duration, in ms. */
  roundMs: number;
  /** Sliding-window size N. */
  windowN: number;
  /** Exploration scale β. */
  beta: number;
  /** Spatial drift stddev δ per round. */
  drift: number;
}

interface FieldDef {
  id: string;
  label: string;
  desc: string;
  min: number;
  max: number;
  step: number;
  def: number;
  fmt: (v: number) => string;
}

const FIELDS: FieldDef[] = [
  { id: 'rounds', label: 'Rounds', desc: 'total casts the agent gets', min: 10, max: 500, step: 5, def: 120, fmt: (v) => v.toFixed(0) },
  { id: 'speed', label: 'Speed', desc: 'seconds per cast', min: 0.1, max: 2.0, step: 0.05, def: 0.35, fmt: (v) => v.toFixed(1) + 's' },
  { id: 'window', label: 'Window N', desc: 'how many recent casts the agent remembers', min: 5, max: 100, step: 1, def: 10, fmt: (v) => v.toFixed(0) },
  { id: 'beta', label: 'Explore β', desc: 'max exploration — scales down automatically once the agent finds the fish', min: 0, max: 2, step: 0.01, def: 0.20, fmt: (v) => v.toFixed(2) },
  { id: 'drift', label: 'Drift δ', desc: 'how fast the fish wander', min: 0, max: 0.03, step: 0.001, def: 0.010, fmt: (v) => v.toFixed(3) },
];

export class StartScreen {
  readonly el: HTMLElement;

  constructor(onPlay: (config: EpisodeConfig) => void) {
    this.el = document.createElement('div');
    this.el.className = 'overlay';
    this.el.innerHTML = `
      <div class="overlay-card">
        <h2>Bandit Fisher</h2>
        <p class="overlay-intro">A learning agent tries to find where the fish are.
        Each cast, it gets a signal — how likely a fish was to bite — and updates its belief.
        Configure the episode and press Play.</p>
        <div class="form-grid"></div>
        <button class="play-btn">▶ Play</button>
      </div>
    `;
    const grid = this.el.querySelector<HTMLDivElement>('.form-grid')!;
    for (const f of FIELDS) {
      const row = document.createElement('div');
      row.className = 'form-row';
      row.innerHTML = `
        <div class="form-row-label">
          <span class="form-row-name">${f.label}</span>
          <span class="form-row-desc">${f.desc}</span>
        </div>
        <div class="form-row-control">
          <input type="range" id="ss-${f.id}" min="${f.min}" max="${f.max}" step="${f.step}" value="${f.def}" />
          <span class="form-row-val" id="ss-${f.id}-val">${f.fmt(f.def)}</span>
        </div>
      `;
      grid.appendChild(row);
      const input = row.querySelector<HTMLInputElement>(`#ss-${f.id}`)!;
      const val = row.querySelector<HTMLElement>(`#ss-${f.id}-val`)!;
      input.addEventListener('input', () => {
        val.textContent = f.fmt(parseFloat(input.value));
      });
    }
    this.el.querySelector<HTMLButtonElement>('.play-btn')!.addEventListener('click', () => {
      const read = (id: string) => parseFloat((this.el.querySelector(`#ss-${id}`) as HTMLInputElement).value);
      const cfg: EpisodeConfig = {
        rounds: Math.round(read('rounds')),
        roundMs: read('speed') * 1000,
        windowN: Math.round(read('window')),
        beta: read('beta'),
        drift: read('drift'),
      };
      onPlay(cfg);
    });
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.el);
  }

  remove(): void {
    this.el.remove();
  }
}
